import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { dedupeLines } from "../lib/dedupe";

const SAMPLE = "apple\nbanana\napple\ncherry\nbanana\ndate";

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      {label}
    </label>
  );
}

export function DedupeTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [trimEach, setTrimEach] = useState(false);
  const [removeEmpty, setRemoveEmpty] = useState(false);
  const [sort, setSort] = useState(false);
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () => dedupeLines(input, { caseInsensitive, trimEach, removeEmpty, sort }),
    [input, caseInsensitive, trimEach, removeEmpty, sort],
  );
  const output = result.lines.join("\n");

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.rd_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.rd_placeholder()}
            spellCheck={false}
            className="min-h-[14rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={`${uid}-out`}>{m.rd_output_label()}</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              disabled={!output}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.rd_copied() : m.rd_copy()}
            </Button>
          </div>
          <textarea
            id={`${uid}-out`}
            readOnly
            data-testid="rd-output"
            value={output}
            spellCheck={false}
            className="min-h-[14rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-border bg-card/40 p-4">
        <Toggle
          checked={caseInsensitive}
          onChange={setCaseInsensitive}
          label={m.rd_case()}
        />
        <Toggle checked={trimEach} onChange={setTrimEach} label={m.rd_trim()} />
        <Toggle
          checked={removeEmpty}
          onChange={setRemoveEmpty}
          label={m.rd_empty()}
        />
        <Toggle checked={sort} onChange={setSort} label={m.rd_sort()} />
        <div className="ml-auto flex gap-2 text-xs font-medium">
          <span
            data-testid="rd-kept"
            className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-emerald-300"
          >
            {m.rd_kept({ n: String(result.lines.length) })}
          </span>
          <span
            data-testid="rd-removed"
            className="rounded-full bg-rose-500/15 px-2.5 py-0.5 text-rose-300"
          >
            {m.rd_removed({ n: String(result.removed) })}
          </span>
        </div>
      </div>
    </div>
  );
}
